import React, { useEffect, useCallback, useState } from 'react';

/**
 * FacebookLoginButton - A reusable component for Facebook OAuth using the standard Facebook SDK
 * @param {string} appId - Facebook App ID from backend config
 * @param {function} onSuccess - Callback function when login succeeds, receives accessToken
 * @param {function} onError - Callback function when login fails
 * @param {string} text - Button text variant: "signin_with" or "signup_with"
 */
const FacebookLoginButton = ({ appId, onSuccess, onError, text = "signin_with" }) => {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const buttonText = text === "signup_with" ? "Sign up with Facebook" : "Sign in with Facebook";

  /**
   * Initialize the Facebook SDK
   */
  const initFacebookSDK = useCallback(() => {
    if (!appId) return;

    window.fbAsyncInit = () => {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
      setSdkLoaded(true);
    };

    // Load the SDK script if not already loaded
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    } else if (window.FB) {
      // SDK already loaded, just reinitialize
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
      setSdkLoaded(true);
    }
  }, [appId]);

  useEffect(() => {
    initFacebookSDK();
  }, [initFacebookSDK]);

  /**
   * Handle Facebook login click
   */
  const handleFacebookLogin = () => {
    if (!window.FB || !sdkLoaded) {
      console.error('Facebook SDK not loaded');
      onError?.('Facebook SDK not loaded');
      return;
    }

    setIsLoading(true);

    window.FB.login(
      (response) => {
        setIsLoading(false);
        if (response.authResponse) {
          const { accessToken } = response.authResponse;
          onSuccess?.(accessToken);
        } else {
          console.error('Facebook login cancelled or failed');
          onError?.('Login cancelled or failed');
        }
      },
      { scope: 'email,public_profile' }
    );
  };

  if (!appId) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleFacebookLogin}
      disabled={!sdkLoaded || isLoading}
      className="facebook-login-btn"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        backgroundColor: '#1877F2',
        color: '#ffffff',
        border: 'none',
        borderRadius: '4px',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: sdkLoaded && !isLoading ? 'pointer' : 'not-allowed',
        opacity: sdkLoaded && !isLoading ? 1 : 0.7,
        minWidth: '240px',
        height: '40px',
        fontFamily: 'Roboto, Arial, sans-serif',
        transition: 'background-color 0.2s ease'
      }}
      onMouseEnter={(e) => {
        if (sdkLoaded && !isLoading) {
          e.target.style.backgroundColor = '#166FE5';
        }
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = '#1877F2';
      }}
      aria-label={buttonText}
      tabIndex={0}
    >
      {/* Facebook Logo SVG */}
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="#ffffff"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
      <span>{isLoading ? 'Loading...' : buttonText}</span>
    </button>
  );
};

export default FacebookLoginButton;

